import type { ReactNode } from "react";
import { MenuItem, TextField } from "@mui/material";
import { FilterModal } from "@/components/FilterModal";
import { SelectEntidade } from "@/components/SelectEntidade";
import { lojaLabel, MESES, type Loja, type RelatorioFiltro } from "@/api/escala.service";
import type { EstadoFiltroRelatorio } from "@/components/escala/useRelatorio";

interface RelatorioFiltroCamposProps {
  values: RelatorioFiltro;
  onChange: (values: RelatorioFiltro) => void;
  lojas: Loja[];
  // Colaboradores por escala é foto do cadastro atual, sem competência —
  // esconde ano/mês nesse caso em vez de mandar um valor que o Method ignora.
  comCompetencia?: boolean;
  // Transferências não tem filtro de cargo (o Method não aceita) — some o
  // campo em vez de mostrar um seletor que não faz nada.
  comCargo?: boolean;
}

// Ano, mês, loja e cargo; loja e cargo vazios viram undefined, que o Method trata como "sem filtro".
export function RelatorioFiltroCampos({
  values,
  onChange,
  lojas,
  comCompetencia = true,
  comCargo = true,
}: RelatorioFiltroCamposProps) {
  const anoAtual = new Date().getFullYear();
  const anos = Array.from({ length: 6 }, (_, i) => anoAtual - i);

  return (
    <>
      {comCompetencia && (
        <>
          <TextField
            select
            label="Ano"
            fullWidth
            size="small"
            value={values.ano}
            onChange={(e) => onChange({ ...values, ano: Number(e.target.value) })}
          >
            {anos.map((ano) => (
              <MenuItem key={ano} value={ano}>
                {ano}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Mês"
            fullWidth
            size="small"
            value={values.mes}
            onChange={(e) => onChange({ ...values, mes: Number(e.target.value) })}
          >
            {MESES.map((nome, i) => (
              <MenuItem key={nome} value={i + 1}>
                {nome}
              </MenuItem>
            ))}
          </TextField>
        </>
      )}
      <SelectEntidade
        label="Loja (vazio = todas)"
        options={lojas}
        value={values.loja_id}
        onChange={(id) => onChange({ ...values, loja_id: id ?? undefined })}
        getLabel={lojaLabel}
        size="small"
      />
      {comCargo && (
        <TextField
          label="Cargo (contém)"
          fullWidth
          size="small"
          value={values.cargo ?? ""}
          onChange={(e) => onChange({ ...values, cargo: e.target.value || undefined })}
        />
      )}
    </>
  );
}

interface FiltroRelatorioProps<F extends RelatorioFiltro> {
  estado: EstadoFiltroRelatorio<F>;
  comCompetencia?: boolean;
  comCargo?: boolean;
  // Campos a mais, depois dos comuns (ex.: nome do colaborador).
  children?: ReactNode;
}

// Botão Filtros de um relatório, ligado ao estado de useFiltroRelatorio.
export function FiltroRelatorio<F extends RelatorioFiltro>({ estado, comCompetencia, comCargo, children }: FiltroRelatorioProps<F>) {
  return (
    <FilterModal activeCount={estado.filtrosAtivos} onApply={estado.applyFilters} onClear={estado.handleClearFilters}>
      <RelatorioFiltroCampos
        values={estado.inputValues}
        onChange={(v) => estado.setInputValues((atual) => ({ ...atual, ...v }))}
        lojas={estado.lojas}
        comCompetencia={comCompetencia}
        comCargo={comCargo}
      />
      {children}
    </FilterModal>
  );
}
